import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useState, useEffect } from 'react';
import { useRecordPayment } from '@/hooks/queries/usePayments';
import { useAccounts } from '@/hooks/queries/useTreasury';
import { useAuthStore } from '@/stores/authStore';
import { toast } from '@/components/ui/toast/useToast';
import { Loader2, DollarSign, Calendar, CreditCard, FileText } from 'lucide-react';
import type { RecordPaymentInput } from '@/types/payments';

interface RecordPaymentModalProps {
  open: boolean;
  onClose: () => void;
  invoice: {
    id: string;
    invoice_number: string;
    client_name: string;
    total: number;
    amount_paid: number;
    amount_outstanding: number;
  } | null;
}

export function RecordPaymentModal({ open, onClose, invoice }: RecordPaymentModalProps) {
  const { company, user } = useAuthStore();
  const { data: accounts = [] } = useAccounts(company?.id || '');
  const recordPayment = useRecordPayment();

  const [amount, setAmount] = useState('');
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split('T')[0]);
  const [paymentMethod, setPaymentMethod] = useState<'cash' | 'check' | 'transfer' | 'card' | 'mobile' | 'ccp'>('cash');
  const [accountId, setAccountId] = useState('');
  const [notes, setNotes] = useState('');

  // Check fields
  const [checkNumber, setCheckNumber] = useState('');
  const [checkDate, setCheckDate] = useState('');
  const [checkBank, setCheckBank] = useState('');
  const [checkDrawerName, setCheckDrawerName] = useState('');

  // Set default values when modal opens
  useEffect(() => {
    if (open && invoice) {
      setAmount(invoice.amount_outstanding.toString());
      setCheckDrawerName(invoice.client_name);
      setPaymentDate(new Date().toISOString().split('T')[0]);
    }
  }, [open, invoice]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!invoice || !company || !user) return;

    // Validation
    if (!accountId) {
      toast.error('Erreur', 'Veuillez sélectionner un compte');
      return;
    }

    const paymentAmount = parseFloat(amount);
    if (isNaN(paymentAmount) || paymentAmount <= 0) {
      toast.error('Erreur', 'Montant invalide');
      return;
    }

    if (paymentMethod === 'check' && !checkNumber) {
      toast.error('Erreur', 'Numéro de chèque requis');
      return;
    }

    try {
      const paymentData: RecordPaymentInput & { companyId: string; userId: string } = {
        invoice_id: invoice.id,
        amount: paymentAmount,
        payment_date: paymentDate,
        payment_method: paymentMethod,
        account_id: accountId,
        description: `Paiement facture ${invoice.invoice_number}`,
        notes,
        companyId: company.id,
        userId: user.id,
      };

      // Add check fields if payment method is check
      if (paymentMethod === 'check') {
        paymentData.check_number = checkNumber;
        paymentData.check_date = checkDate || paymentDate;
        paymentData.check_bank = checkBank;
        paymentData.check_drawer_name = checkDrawerName || invoice.client_name;
      }

      await recordPayment.mutateAsync(paymentData);

      toast.success(
        'Paiement enregistré',
        `${formatCurrency(paymentAmount)} ajouté à la facture ${invoice.invoice_number}`
      );

      // Reset form
      setAmount('');
      setPaymentDate(new Date().toISOString().split('T')[0]);
      setPaymentMethod('cash');
      setAccountId('');
      setNotes('');
      setCheckNumber('');
      setCheckDate('');
      setCheckBank('');
      setCheckDrawerName('');

      onClose();
    } catch (error) {
      console.error('Error recording payment:', error);
      toast.error('Erreur', 'Impossible d\'enregistrer le paiement');
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('ar-DZ', {
      style: 'currency',
      currency: 'DZD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  if (!invoice) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl">
            تسجيل دفعة / Enregistrer un paiement
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Invoice Summary */}
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600">Facture</p>
                <p className="font-semibold text-lg">{invoice.invoice_number}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Client</p>
                <p className="font-medium">{invoice.client_name}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Total facture</p>
                <p className="font-bold text-xl">{formatCurrency(invoice.total)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Déjà payé</p>
                <p className="font-bold text-green-600">{formatCurrency(invoice.amount_paid)}</p>
              </div>
              <div className="col-span-2">
                <p className="text-sm text-gray-600">Montant restant</p>
                <p className="font-bold text-2xl text-orange-600">
                  {formatCurrency(invoice.amount_outstanding)}
                </p>
              </div>
            </div>
          </div>

          {/* Payment Amount */}
          <div className="space-y-2">
            <Label htmlFor="amount" className="flex items-center gap-2">
              <DollarSign className="w-4 h-4" />
              المبلغ المدفوع / Montant du paiement (DA) *
            </Label>
            <Input
              id="amount"
              type="number"
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0.00"
              required
              className="text-lg font-semibold"
            />
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setAmount(invoice.amount_outstanding.toString())}
              >
                Montant complet
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setAmount((invoice.amount_outstanding / 2).toFixed(2))}
              >
                50%
              </Button>
            </div>
          </div>

          {/* Payment Date */}
          <div className="space-y-2">
            <Label htmlFor="paymentDate" className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              تاريخ الدفع / Date du paiement *
            </Label>
            <Input
              id="paymentDate"
              type="date"
              value={paymentDate}
              onChange={(e) => setPaymentDate(e.target.value)}
              max={new Date().toISOString().split('T')[0]}
              required
            />
          </div>

          {/* Account Selection */}
          <div className="space-y-2">
            <Label htmlFor="account">الحساب / Compte *</Label>
            <Select value={accountId} onValueChange={setAccountId} required>
              <SelectTrigger id="account">
                <SelectValue placeholder="Sélectionnez un compte" />
              </SelectTrigger>
              <SelectContent>
                {accounts.length === 0 ? (
                  <div className="p-2 text-sm text-gray-500">Aucun compte disponible</div>
                ) : (
                  accounts
                    .filter((a) => a.is_active)
                    .map((account) => (
                      <SelectItem key={account.id} value={account.id}>
                        {account.name} ({formatCurrency(account.current_balance)})
                      </SelectItem>
                    ))
                )}
              </SelectContent>
            </Select>
          </div>

          {/* Payment Method */}
          <div className="space-y-2">
            <Label htmlFor="paymentMethod" className="flex items-center gap-2">
              <CreditCard className="w-4 h-4" />
              وسيلة الدفع / Moyen de paiement *
            </Label>
            <Select
              value={paymentMethod}
              onValueChange={(value: any) => setPaymentMethod(value)}
              required
            >
              <SelectTrigger id="paymentMethod">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="cash">💵 نقد / Espèces</SelectItem>
                <SelectItem value="check">🧾 شيك / Chèque</SelectItem>
                <SelectItem value="transfer">🏦 تحويل بنكي / Virement</SelectItem>
                <SelectItem value="card">💳 بطاقة / Carte</SelectItem>
                <SelectItem value="ccp">📮 CCP</SelectItem>
                <SelectItem value="mobile">📱 Mobile (Baridi Mob)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Check Fields (only if payment method is check) */}
          {paymentMethod === 'check' && (
            <div className="space-y-4 p-4 border-2 border-orange-200 bg-orange-50 rounded-lg">
              <h3 className="font-semibold text-lg flex items-center gap-2">
                <FileText className="w-5 h-5" />
                تفاصيل الشيك / Détails du chèque
              </h3>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="checkNumber">رقم الشيك / N° chèque *</Label>
                  <Input
                    id="checkNumber"
                    value={checkNumber}
                    onChange={(e) => setCheckNumber(e.target.value)}
                    placeholder="Ex: 1234567"
                    required={paymentMethod === 'check'}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="checkDate">تاريخ الشيك / Date chèque</Label>
                  <Input
                    id="checkDate"
                    type="date"
                    value={checkDate}
                    onChange={(e) => setCheckDate(e.target.value)}
                  />
                  <p className="text-xs text-gray-500">
                    Si vide, date du paiement sera utilisée
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="checkBank">البنك / Banque</Label>
                  <Input
                    id="checkBank"
                    value={checkBank}
                    onChange={(e) => setCheckBank(e.target.value)}
                    placeholder="Ex: BNA"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="checkDrawer">صاحب الشيك / Tireur</Label>
                  <Input
                    id="checkDrawer"
                    value={checkDrawerName}
                    onChange={(e) => setCheckDrawerName(e.target.value)}
                    placeholder={invoice.client_name}
                  />
                </div>
              </div>

              <div className="p-3 bg-orange-100 border border-orange-300 rounded">
                <p className="text-sm text-orange-800">
                  ⚠️ تذكير: الشيكات صالحة لمدة 180 يوماً في الجزائر
                  <br />
                  Rappel: Les chèques sont valables 180 jours en Algérie
                </p>
              </div>
            </div>
          )}

          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="notes">ملاحظات / Notes</Label>
            <Textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Notes supplémentaires..."
              rows={3}
            />
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="flex-1"
              disabled={recordPayment.isPending}
            >
              إلغاء / Annuler
            </Button>
            <Button
              type="submit"
              className="flex-1 bg-green-600 hover:bg-green-700"
              disabled={recordPayment.isPending}
            >
              {recordPayment.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              تسجيل الدفع / Enregistrer le paiement
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
